import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  IconButton,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Popover,
  TextField,
  Tooltip, // Import Tooltip
  Checkbox,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import SearchIcon from "@mui/icons-material/Search";
import { styled } from "@mui/material/styles";
import { dateConvertMiladiToShamsi } from "common/method/date";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "& td": {
    height: "50px !important",
    maxWidth: 150,
  },
}));
const separatorNumberWithComma1 = (number) => {
  if (typeof number !== "number" || isNaN(number)) return "-";
  return number.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};
const DataTable = ({
  columns,
  data,
  onActionClick,
  actionLabel = "Action",
  pageSize = 10,
  enablePagination = true,
  isLoading = false,
}) => {
  const [page, setPage] = useState(1);
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [anchorEl, setAnchorEl] = useState(null);
  const [filters, setFilters] = useState({});

  const separatorNumberWithComma = (number) => {
    if (typeof number !== "number") return "-";
    return number.toFixed(3).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };
  const handleSort = (column) => {
    const isAsc = sortColumn === column && sortDirection === "asc";
    setSortColumn(column);
    setSortDirection(isAsc ? "desc" : "asc");
  };

  const sortedData = data?.slice()?.sort((a, b) => {
    if (!sortColumn) return 0;
    const aValue = a[sortColumn];
    const bValue = b[sortColumn];
    return (aValue < bValue ? -1 : 1) * (sortDirection === "asc" ? 1 : -1);
  });

  const filteredData = sortedData?.filter((row) =>
    Object.keys(filters).every((key) => {
      if (!filters[key]) return true;
      return row[key]
        ?.toString()
        .toLowerCase()
        .includes(filters[key].toLowerCase());
    }),
  );

  const paginatedData = enablePagination
    ? filteredData?.slice((page - 1) * pageSize, page * pageSize)
    : filteredData;
  const handleSearchIconClick = (event, field) => {
    setAnchorEl({ anchor: event.currentTarget, field });
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleFilterChange = (event) => {
    const { field } = anchorEl;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [field]: event.target.value,
    }));
  };

  const calculateSum = (field) => {
    return data?.reduce((total, row) => {
      return total + (typeof row[field] === "number" ? row[field] : "-");
    }, 0);
  };

  return (
    <div>
      <TableContainer>
        <Table stickyHeader aria-label="data-table">
          <TableHead>
            <TableRow>
              {columns?.map((col, index) => (
                <TableCell
                  key={index}
                  align="center"
                  style={{
                    color: "black",
                    fontSize: 12,
                    maxWidth: 100,
                    minWidth: 80,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        color: "black",
                        fontSize: 12,
                        maxWidth: "fit-content",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        minWidth: 50,
                      }}
                    >
                      <Tooltip title={col.label} arrow>
                        {" "}
                        <span>{col.label}</span>
                      </Tooltip>
                    </div>
                    {col.sortable && (
                      <IconButton onClick={() => handleSort(col.field)}>
                        {sortColumn === col.field ? (
                          sortDirection === "asc" ? (
                            <ArrowUpwardIcon style={{ fontSize: 12 }} />
                          ) : (
                            <ArrowDownwardIcon style={{ fontSize: 12 }} />
                          )
                        ) : (
                          <ArrowDownwardIcon style={{ fontSize: 12 }} />
                        )}
                      </IconButton>
                    )}
                    {col.searchable && (
                      <IconButton
                        onClick={(e) => handleSearchIconClick(e, col.field)}
                      >
                        <SearchIcon style={{ fontSize: 12 }} />
                      </IconButton>
                    )}
                  </div>{" "}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <StyledTableRow>
                <TableCell align="center" colSpan={columns.length}>
                  <CircularProgress />
                </TableCell>
              </StyledTableRow>
            ) : paginatedData?.length > 0 ? (
              paginatedData.map((row, index) => (
                <StyledTableRow key={index}>
                  {columns.map((col, colIndex) => (
                    <TableCell
                      key={colIndex}
                      align="center"
                      style={{
                        fontSize: 11,
                        direction: "ltr",
                        textAlign: "center",
                      }}
                    >
                      {col.field === "has_error" ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Checkbox
                            color="error"
                            checked={!!row?.body?.[col.field]}
                            readOnly
                            style={{
                              width: "20px",
                              height: "20px",
                              cursor: "not-allowed",
                            }}
                          />
                        </div>
                      ) : col.field === "actions" ? (
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={() => onActionClick(row?.body?.id)}
                        >
                          {actionLabel}
                        </Button>
                      ) : col.type === "date" ? (
                        dateConvertMiladiToShamsi(row?.body?.[col.field])
                      ) : col.type === "toFixed" ? (
                        separatorNumberWithComma1(row?.body?.[col.field])
                      ) : col.type === "option" ? (
                        col?.format(row?.body?.[col.field])
                      ) : col.type === "number" ? (
                        separatorNumberWithComma(row?.body?.[col.field])
                      ) : (
                        row?.body?.[col.field] || "-"
                      )}
                    </TableCell>
                  ))}
                </StyledTableRow>
              ))
            ) : (
              <StyledTableRow>
                <TableCell align="center" colSpan={columns.length}>
                  اطلاعاتی یافت نشد .{" "}
                </TableCell>
              </StyledTableRow>
            )}
            <StyledTableRow>
              {isLoading ? (
                <StyledTableRow>
                  <TableCell align="center" colSpan={columns.length}>
                    {null}
                  </TableCell>
                </StyledTableRow>
              ) : (
                columns.map((col, colIndex) => (
                  <TableCell
                    key={colIndex}
                    align="center"
                    style={{
                      fontSize: 11,
                      direction: "ltr",
                      fontWeight: "bold",
                    }}
                  >
                    {col.type === "number" || col.type === "toFixed"
                      ? separatorNumberWithComma(calculateSum(col.field))
                      : colIndex === 0
                        ? "جمع کل"
                        : "-"}
                  </TableCell>
                ))
              )}
            </StyledTableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {enablePagination && filteredData?.length > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            margin: "10px 0",
          }}
        >
          <Pagination
            count={Math.ceil(filteredData.length / pageSize)}
            page={page}
            onChange={(event, value) => setPage(value)}
          />
        </div>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl?.anchor}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <TextField
          size="small"
          placeholder="جستجو..."
          value={filters[anchorEl?.field] || ""}
          onChange={handleFilterChange}
          style={{ margin: "10px" }}
        />
      </Popover>
    </div>
  );
};

export default DataTable;
